import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { get, keys, values, map } from 'lodash';
import { cssVariables } from '@env/css-vars';
import { ScenarioPlannerService } from '@app/shared/services/api/scenario-planner.service';
import { saveFile } from '@app/shared/utility';
import { SaveScenarioExcelDialogComponent } from '@app/energy-map/dialog/save-scenario-excel-dialog/save-scenario-excel-dialog.component';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { ExcelService, Excel } from '@app/shared/services/api/excel.service';
import { catchError, finalize, first, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { RecommendationsDto, ScenarioPlannerResponseDto } from '@app/shared/models/scenario-planner';

@Component({
  selector: 'app-scenario-item',
  templateUrl: './scenario-item.component.html',
  styleUrls: ['./scenario-item.component.scss'],
})
export class ScenarioItemComponent implements OnInit {
  @Input() scenario: any;
  @Output() saveExcel = new EventEmitter();
  @Output() deleteEvent = new EventEmitter();
  @Output() loadEvent = new EventEmitter();

  show = false;

  constructor(
    private excelService: ExcelService,
    private matDialog: MatDialog,
    private scenarioPlannerService: ScenarioPlannerService,
    private translate: TranslateService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {}

  public ngOnInit(): void {
    const municipality = this.userMunicipalitiesService.municipalities.find((a) => a.key === this.scenario.municipalityKey);
    this.scenario.municipalityName = get(municipality, 'name');
  }

  public deleteItem(value: any): void {
    this.deleteEvent.emit(value);
  }

  public loadItem(value: any): void {
    this.loadEvent.emit(value);
  }

  public saveInExcel(id: any): void {
    this.translate
      .get('module.modals.saveScenarioExcel.dataEnteredText')
      .pipe(first())
      .subscribe((text: string) => {
        const matRef = this.matDialog.open(SaveScenarioExcelDialogComponent, {
          data: text,
          panelClass: ['-md', '-excel-panel'],
        });

        matRef.afterClosed().subscribe((res) => {
          if (res) {
            this.downloadExcelById(id);
          }
        });
      });
  }

  public downloadExcelById(id: string): void {
    let excelData: Excel;
    this.show = true;

    this.scenarioPlannerService
      .getById(id)
      .pipe(
        switchMap((data: ScenarioPlannerResponseDto) => {
          const csvData = data.recommendations.map((recommendation: RecommendationsDto, index: number) => ({
            Nummer: index + 1,
            Ortsteil: recommendation.municipality,
            Straße: recommendation.street,
            Hausnummer: recommendation.locationInfo,
            Brennstellennummer: recommendation.bstNumber,
            'Leuchtentyp alt': recommendation.currentModel, // eslint-disable-line
            'Leuchtentyp neu': recommendation.suggestedModel, // eslint-disable-line
          }));

          excelData = {
            fileName: `${data.name}.xlsx`,
            sheetName: `Sheet1`,
            headers: keys(csvData[0]),
            data: map(csvData, values),
            protectedColumns: [],
            templateCode: '',
            headerBgColor: cssVariables['--ep-primary-color'],
            secretPhrase: '',
          };

          return this.excelService.generateExcel(excelData).pipe(catchError(() => of(null)));
        }),
        finalize(() => (this.show = false)),
        first()
      )
      .subscribe((xlsx) => {
        if (!!xlsx && !!excelData) {
          const blob = new Blob([xlsx], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          saveFile(blob, excelData.fileName);
        }
      });
  }

  loadFile(data: any) {
    const link = document.createElement('a');
    const blob = new Blob([data], { type: data.type });
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('target', '_blank');
      link.setAttribute('download', this.scenario.name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    this.show = false;
  }
}
